import { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Checkbox, Modal, Popup, Select, Table } from "semantic-ui-react";
import { toast } from "../..";
import { UserData } from "../../actions/authentificationActions";
import {
    changeUserRole,
    changeUserTso,
    deleteUser,
    toggleLocalLoginEnabled,
    toggleUserEnabled,
} from "../../services/userService";
import { AppState } from "../../store/configureStore";
import { ITso } from "./CreateUserForm";
import { ChangePassword } from "../UserProfile/ChangePassword";

interface IProps {
    ind: string;
    user: UserData;
    tsos: ITso[];
    handleRemoveUser: (user: UserData) => void;
    handleUpdateUserRole: (user: UserData, newRole: string) => void;
    handleUpdateUserTso: (user: UserData, newTso: string) => void;
    handleToggleUserEnabled: (user: UserData, isDisabled: boolean) => void;
    isLocalLoginEnabled: boolean;
    handleToggleLocalLoginUserEnabled: (user: UserData, localLoginEnabled: boolean) => void;
}

export const UsersTableRow = ({
    ind,
    user,
    tsos,
    handleRemoveUser,
    handleUpdateUserRole,
    handleUpdateUserTso,
    handleToggleUserEnabled,
    isLocalLoginEnabled,
    handleToggleLocalLoginUserEnabled,
}: IProps) => {
    const currUser = useSelector((state: AppState) => state.user);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [enableModalOpen, setEnableModalOpen] = useState(false);
    const [localLoginModalOpen, setLocalLoginModalOpen] = useState(false);
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);

    const handleChangeUserRole = async (newRole: string) => {
        // Role to TSO and TSOAdmin can only happen if user has a TSO assigned!
        if (newRole === "TSO" || newRole === "TSOAdmin") {
            if (!user.tso) {
                toast("User must have a TSO assigned to change role to TSO or TSOAdmin", false);
                return;
            }
        }

        const res = await changeUserRole(user.guid!, newRole);
        if (res.ok) {
            handleUpdateUserRole(user, newRole);
            toast("User's role successfully updated", true);
        } else toast("Unable to update user's role", false);
    };

    const handleDeleteUser = async () => {
        const res = await deleteUser(user.guid!);
        if (res.ok) {
            handleRemoveUser(user);
            toast("User successfully deleted", true);
        } else toast("Unable to delete user", false);
        setDeleteModalOpen(false);
    };

    const handleToggleEnableUser = async () => {
        const res = await toggleUserEnabled(user.guid!);
        if (res.ok) {
            handleToggleUserEnabled(user, !user.isDisabled);
            toast(`User successfully ${user.isDisabled ? "enabled" : "disabled"}`, true);
        } else toast(`Unable to ${user.isDisabled ? "enable" : "disable"} user`, false);
        setEnableModalOpen(false);
    };

    const handleToggleLocalLoginEnabled = async () => {
        const res = await toggleLocalLoginEnabled(user.guid!);

        if (res.ok) {
            handleToggleLocalLoginUserEnabled(user, !user.localLoginEnabled);
            toast(
                `User's local login successfully ${
                    user.localLoginEnabled ? "disabled" : "enabled"
                }`,
                true
            );
        } else {
            const resMessage = await res.text();
            toast(resMessage ?? "Unable to toggle user's local login", false, 5000);
        }
        setLocalLoginModalOpen(false);
    };

    const handleChangeUserTso = async (newTso: string) => {
        if (currUser.role === "TSOAdmin") {
            toast("TSOAdmin cannot edit TSO for users", false);
            return;
        }
        const res = await changeUserTso(user.guid!, newTso);
        if (res.ok) {
            handleUpdateUserTso(user, newTso);
            toast("User's TSO successfully updated", true);
        } else toast("Unable to update user's TSO", false);
    };

    return (
        <>
            <Table.Row id={`tableRow-usersTableRow-a-${ind}`}>
                <Table.Cell id={`tableCell-usersTableRow-a-${ind}`}>
                    {user.firstName} {user.lastName}
                </Table.Cell>
                <Table.Cell id={`tableCell-usersTableRow-b-${ind}`}>{user.username}</Table.Cell>
                <Table.Cell id={`tableCell-usersTableRow-c-${ind}`}>
                    <Select
                        id={`select-usersTableRow-a-${ind}`}
                        selectOnBlur={false}
                        value={user.tso}
                        options={tsos}
                        clearable={user.role === "Admin" || user.role === "SuperAdmin"}
                        disabled={currUser.role === "TSOAdmin"}
                        onChange={(e, data) => handleChangeUserTso(data.value as string)}
                    />
                </Table.Cell>
                <Table.Cell id={`tableCell-usersTableRow-d-${ind}`}>
                    <Select
                        id={`select-usersTableRow-b-${ind}`}
                        selectOnBlur={false}
                        value={user.role}
                        options={[
                            { key: "user", text: "User", value: "User" },
                            { key: "tso", text: "TSO", value: "TSO" },
                            {
                                key: "tsoadmin",
                                text: "TSO Admin",
                                value: "TSOAdmin",
                            },
                            ...(currUser.role === "SuperAdmin"
                                ? [{ key: "admin", text: "Admin", value: "Admin" }]
                                : []),
                        ]}
                        onChange={(e, data) => handleChangeUserRole(data.value as string)}
                    />
                </Table.Cell>
                <Table.Cell id={`tableCell-usersTableRow-de-${ind}`}>
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString("de") : "Never"}
                </Table.Cell>
                <Table.Cell id={`tableCell-usersTableRow-e-${ind}`}>
                    <Checkbox
                        id={`checkbox-usersTableRow-e-${ind}`}
                        readOnly
                        checked={!user.isDisabled}
                    />
                </Table.Cell>
                {isLocalLoginEnabled && (
                    <Table.Cell id={`tableCell-usersTableRow-e2-${ind}`}>
                        <Checkbox
                            id={`checkbox-usersTableRow-e2-${ind}`}
                            readOnly
                            checked={user.localLoginEnabled}
                        />
                    </Table.Cell>
                )}
                <Table.Cell id={`tableCell-usersTableRow-f-${ind}`} collapsing textAlign="center">
                    <Popup
                        id={`popup-useresTableRow-a-${ind}`}
                        inverted
                        content={user.isDisabled ? "Enable user" : "Disable user"}
                        trigger={
                            <Button
                                id={`button-usersTableRow-a-${ind}`}
                                color={user.isDisabled ? "green" : "yellow"}
                                icon={user.isDisabled ? "check" : "ban"}
                                size="large"
                                onClick={() => setEnableModalOpen(true)}
                            />
                        }
                    />
                    {isLocalLoginEnabled &&
                        (currUser.role === "SuperAdmin" || currUser.role === "Admin") && (
                            <>
                                <Popup
                                    id={`popup-useresTableRow-c-${ind}`}
                                    inverted
                                    content={
                                        user.localLoginEnabled
                                            ? "Disable local login"
                                            : "Enable local login"
                                    }
                                    trigger={
                                        <Button
                                            id={`button-usersTableRow-c-${ind}`}
                                            color={user.localLoginEnabled ? "yellow" : "green"}
                                            icon="database"
                                            size="large"
                                            onClick={() => setLocalLoginModalOpen(true)}
                                        />
                                    }
                                />
                                <Popup
                                    id={`popup-useresTableRow-d-${ind}`}
                                    inverted
                                    content="Reset password"
                                    trigger={
                                        <Button
                                            id={`button-usersTableRow-d-${ind}`}
                                            icon="key"
                                            size="large"
                                            onClick={() => setResetPasswordModalOpen(true)}
                                        />
                                    }
                                />
                            </>
                        )}
                    <Popup
                        id={`popup-useresTableRow-b-${ind}`}
                        inverted
                        content="Delete user"
                        trigger={
                            <Button
                                id={`button-usersTableRow-b-${ind}`}
                                negative
                                icon="delete"
                                size="large"
                                onClick={() => setDeleteModalOpen(true)}
                            />
                        }
                    />
                </Table.Cell>
            </Table.Row>

            <Modal
                id={`modal-usersTableRow-a-${ind}`}
                dimmer="inverted"
                size="mini"
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
            >
                <Modal.Header id={`modal-header-delete-usersTableRow-${ind}`}>
                    Are you sure you want to delete this user?
                </Modal.Header>
                <Modal.Actions id={`modal-actions-usersTableRow-a-${ind}`}>
                    <Button
                        id={`button-no-usersTableRow-a-${ind}`}
                        content="No"
                        onClick={() => setDeleteModalOpen(false)}
                    />
                    <Button
                        id={`button-yes-usersTableRow-a-${ind}`}
                        primary
                        content="Yes"
                        onClick={handleDeleteUser}
                    />
                </Modal.Actions>
            </Modal>

            <Modal
                id={`modal-usersTableRow-b-${ind}`}
                dimmer="inverted"
                size="mini"
                open={enableModalOpen}
                onClose={() => setEnableModalOpen(false)}
            >
                <Modal.Header id={`modal-header-usersTableRow-b-${ind}`}>
                    Are you sure you want to {user.isDisabled ? "enable" : "disable"} this user?
                </Modal.Header>
                <Modal.Actions id={`modal-actions-usersTableRow-b-${ind}`}>
                    <Button
                        id={`button-no-usersTableRow-b-${ind}`}
                        content="No"
                        onClick={() => setEnableModalOpen(false)}
                    />
                    <Button
                        id={`button-yes-usersTableRow-b-${ind}`}
                        primary
                        content="Yes"
                        onClick={handleToggleEnableUser}
                    />
                </Modal.Actions>
            </Modal>

            <Modal
                id={`modal-usersTableRow-c-${ind}`}
                dimmer="inverted"
                size="mini"
                open={localLoginModalOpen}
                onClose={() => setLocalLoginModalOpen(false)}
            >
                <Modal.Header id={`modal-header-usersTableRow-c-${ind}`}>
                    Are you sure you want to {user.localLoginEnabled ? "disable" : "enable"} local
                    login for this user?
                </Modal.Header>
                <Modal.Actions id={`modal-actions-usersTableRow-c-${ind}`}>
                    <Button
                        id={`button-no-usersTableRow-c-${ind}`}
                        content="No"
                        onClick={() => setLocalLoginModalOpen(false)}
                    />
                    <Button
                        id={`button-yes-usersTableRow-c-${ind}`}
                        primary
                        content="Yes"
                        onClick={handleToggleLocalLoginEnabled}
                    />
                </Modal.Actions>
            </Modal>

            <Modal
                id={`modal-usersTableRow-d-${ind}`}
                dimmer="inverted"
                size="mini"
                open={resetPasswordModalOpen}
                onClose={() => setResetPasswordModalOpen(false)}
            >
                <Modal.Header id={`modal-header-usersTableRow-d-${ind}`}>
                    Enter new password. User will be able to change it after login.
                </Modal.Header>
                <Modal.Content id={`modal-content-usersTableRow-d-${ind}`}>
                    <ChangePassword user={user} adminResetMode />
                </Modal.Content>
            </Modal>
        </>
    );
};
